import axios from 'axios';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import EsriPolygon from '@arcgis/core/geometry/Polygon';
import TimeExtent from '@arcgis/core/time/TimeExtent';
import { WGS84_SPATIAL_REFERENCE } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/spatialReference';
import { arcgisGeometryToGeoJson } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/arcgisGeometryToGeoJson';
import config from 'constants/apiConfig';
import { isFeedUri } from 'components/MapPage/CorporateViewTabs/EventsTab/externalFeeds';
import type { IcsIncidentLocation } from 'hooks/icsLocation/icsIncidentLocation.types';
import { collectFeatureLayers } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/useExternalLayerTimeExtent';
import type { ExternalLayerTimeFilter } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import {
  graphicToAttributeSnapshot,
  internalRowToAttributeSnapshot,
} from './layerSnapshotCompact';
import type { LegendClassFilter } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/legendClassFilter';
import { buildAttributeFiltersWhereClause } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/legendClassFilter';
import {
  geometryIntersectsArea,
  type AiSpatialFilter,
} from './aiMapSpatialFilter';

export { buildAttributeFiltersWhereClause } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/legendClassFilter';

export {
  graphicToAttributeSnapshot,
  internalRowToAttributeSnapshot,
  stripFeatureGeometry,
  stripGeometriesFromRealtimeSelections,
} from './layerSnapshotCompact';

export type FeatureSnapshotCoverage = 'complete' | 'sample';

export type LayerAttributeFilterForAI = {
  field: string;
  value: string | number | boolean;
};

export type SelectedLayerForAI = {
  kind: 'internal' | 'external';
  id: string;
  name: string;
  versionId?: string;
  serviceUrl?: string | null;
  arcgisItemId?: string | null;
  featureSnapshot?: unknown[];
  /**
   * Whether the snapshot enumerated the full layer (`complete`) or only a
   * capped sample.
   */
  featureSnapshotCoverage?: FeatureSnapshotCoverage;
  timeExtent?: { startIso: string; endIso: string };
  timeFilterLabel?: string;
  /** Attribute used for datetime filtering (e.g. capture_date, displaydate). */
  timeFilterField?: string;
  /** Map legend class constraints (e.g. fema_classification=Destroyed). */
  attributeFilters?: LayerAttributeFilterForAI[];
  /** Chip label for active legend class filter (e.g. "Destroyed"). */
  legendFilterLabel?: string;
  /**
   * Drawn area or clicked polygon (WGS84). Prefetch keeps only intersecting
   * features' attributes.
   */
  spatialFilter?: AiSpatialFilter;
  /** Chip label: "Area on map" or "Selected: {name}". */
  spatialFilterLabel?: string;
  pickedFeatureName?: string;
  /**
   * Ephemeral ArcGIS OAuth/user token for server-side queries (userAuth
   * layers). Never persist in chat history — stripped by
   * compactDisasterChatPayload.
   */
  arcgisUserToken?: string;
};

/** Page size for client-side full enumeration (not the discovery sample size). */
const PAGE_SIZE = 500;
/**
 * Safety ceiling — if hit, mark coverage as sample rather than silently
 * truncating as complete.
 */
const MAX_PREFETCH_FEATURES = 50000;

export type PrefetchLayerFeaturesOptions = {
  /** Active map time-slider windows keyed by external layer id. */
  timeFiltersByLayerId?: Record<string, ExternalLayerTimeFilter>;
  /** Map runtime layers for an external layer id (prefer querying these). */
  getAddedExternalLayers?: (layerId: string) => __esri.Layer[] | undefined;
};

type PrefetchResult = {
  features: unknown[];
  /** Absent when the client could not run a real query (do not claim complete). */
  coverage?: FeatureSnapshotCoverage;
};

async function fetchInternalFeatures(
  versionId: string,
  spatialFilter?: AiSpatialFilter
): Promise<PrefetchResult> {
  const resp = await axios.get<IcsIncidentLocation[]>(
    `${config.apiGateway.ics_locations}/by-version/${versionId}`
  );
  const rows = (resp.data ?? []).filter((row) =>
    spatialFilter ? geometryIntersectsArea(row.geometry, spatialFilter) : true
  );
  if (rows.length > MAX_PREFETCH_FEATURES) {
    return {
      features: rows
        .slice(0, MAX_PREFETCH_FEATURES)
        .map((row) => internalRowToAttributeSnapshot(row)),
      coverage: 'sample',
    };
  }
  return {
    features: rows.map((row) => internalRowToAttributeSnapshot(row)),
    coverage: 'complete',
  };
}

function spatialFilterToArcgisPolygon(filter: AiSpatialFilter): __esri.Polygon {
  if (filter.type === 'Polygon') {
    return new EsriPolygon({
      rings: filter.coordinates,
      spatialReference: WGS84_SPATIAL_REFERENCE,
    });
  }
  return new EsriPolygon({
    rings: filter.coordinates.flatMap((polygon) => polygon),
    spatialReference: WGS84_SPATIAL_REFERENCE,
  });
}

/**
 * Page FeatureLayer.queryFeatures until all features are loaded (or safety
 * ceiling). Attributes only — returnGeometry false unless a spatialFilter needs
 * a client-side intersect fallback.
 */
async function queryFeatureLayerPaged(
  layer: FeatureLayer,
  options?: {
    timeExtent?: { startIso: string; endIso: string };
    /**
     * When set, used as Query.where (e.g. legend class filter on detached
     * layers).
     */
    where?: string;
    spatialFilter?: AiSpatialFilter;
  }
): Promise<PrefetchResult> {
  await layer.load();
  const all: unknown[] = [];
  let start = 0;
  let exhausted = false;
  const where = options?.where?.trim() || '1=1';
  const spatialFilter = options?.spatialFilter;
  const queryGeometry = spatialFilter
    ? spatialFilterToArcgisPolygon(spatialFilter)
    : undefined;

  while (!exhausted && all.length < MAX_PREFETCH_FEATURES) {
    const query: __esri.QueryProperties = {
      where,
      returnGeometry: Boolean(spatialFilter),
      outFields: ['*'],
      num: PAGE_SIZE,
      start,
    };
    if (options?.timeExtent) {
      query.timeExtent = new TimeExtent({
        start: new Date(options.timeExtent.startIso),
        end: new Date(options.timeExtent.endIso),
      });
    }
    if (queryGeometry) {
      query.geometry = queryGeometry;
      query.spatialRelationship = 'intersects';
    }
    const { features } = await layer.queryFeatures(query);
    if (spatialFilter) {
      for (const graphic of features) {
        if (!graphic.geometry) {
          all.push(graphicToAttributeSnapshot(graphic));
          continue;
        }
        try {
          const geoJson = await arcgisGeometryToGeoJson(graphic.geometry);
          if (geometryIntersectsArea(geoJson, spatialFilter)) {
            all.push(graphicToAttributeSnapshot(graphic));
          }
        } catch {
          all.push(graphicToAttributeSnapshot(graphic));
        }
      }
    } else {
      all.push(...features.map((g) => graphicToAttributeSnapshot(g)));
    }
    if (features.length < PAGE_SIZE) {
      exhausted = true;
    } else {
      start += features.length;
    }
  }

  if (!exhausted && all.length >= MAX_PREFETCH_FEATURES) {
    return { features: all, coverage: 'sample' };
  }
  return { features: all, coverage: 'complete' };
}

/**
 * Prefer querying the map's FeatureLayer when the AI selection is scoped the
 * same way as the map (legend class). For full-layer selections, use a detached
 * URL query so the map's definitionExpression cannot keep returning only the
 * previously filtered class.
 */
async function fetchExternalFeatures(
  layer: SelectedLayerForAI,
  options?: PrefetchLayerFeaturesOptions
): Promise<PrefetchResult> {
  const timeFilter = options?.timeFiltersByLayerId?.[layer.id];
  const timeExtent =
    layer.timeExtent ??
    (timeFilter
      ? { startIso: timeFilter.startIso, endIso: timeFilter.endIso }
      : undefined);
  const filterWhere = buildAttributeFiltersWhereClause(layer.attributeFilters);
  const wantsFullLayer = !layer.attributeFilters?.length;
  const spatialFilter = layer.spatialFilter;

  const mapLayers = options?.getAddedExternalLayers?.(layer.id);
  const mapFeatureLayers = collectFeatureLayers(mapLayers);

  if (mapFeatureLayers.length > 0) {
    const fl = mapFeatureLayers[0];
    const fromMapLayer =
      fl.timeExtent?.start && fl.timeExtent?.end
        ? {
            startIso: fl.timeExtent.start.toISOString(),
            endIso: fl.timeExtent.end.toISOString(),
          }
        : undefined;

    // Full-layer AI selection: briefly clear map legend definitionExpression so
    // prefetch is not stuck on a prior class (detached URL queries often lack
    // the portal/apiKey auth the map layer already has).
    if (wantsFullLayer) {
      const prevDef = fl.definitionExpression;
      try {
        if (prevDef) {
          fl.definitionExpression = null;
        }
        return await queryFeatureLayerPaged(fl, {
          timeExtent: fromMapLayer ?? timeExtent,
          where: '1=1',
          spatialFilter,
        });
      } finally {
        if (prevDef) {
          fl.definitionExpression = prevDef;
        }
      }
    }

    // Category selection: definitionExpression already scopes the instance.
    if (fl.definitionExpression) {
      return queryFeatureLayerPaged(fl, { spatialFilter });
    }
    return queryFeatureLayerPaged(fl, {
      timeExtent: fromMapLayer ?? timeExtent,
      where: filterWhere,
      spatialFilter,
    });
  }

  // No map layer and no URL — cannot claim a complete enumeration.
  if (!layer.serviceUrl) {
    return { features: [] };
  }
  const detached = new FeatureLayer({ url: layer.serviceUrl });
  return queryFeatureLayerPaged(detached, {
    timeExtent,
    where: filterWhere || '1=1',
    spatialFilter,
  });
}

function withTimeMeta(
  layer: SelectedLayerForAI,
  timeFiltersByLayerId?: Record<string, ExternalLayerTimeFilter>
): SelectedLayerForAI {
  const filter = timeFiltersByLayerId?.[layer.id];
  if (!filter) return layer;
  return {
    ...layer,
    timeExtent: {
      startIso: filter.startIso,
      endIso: filter.endIso,
    },
    timeFilterLabel: filter.label,
  };
}

/**
 * Normalize Layers selections before send/prefetch.
 *
 * - Collapse duplicate kind:id rows
 * - If a full-layer entry exists (no attributeFilters), it wins over category
 *   variants so switching to "all" cannot keep a Destroyed/class row around
 */
export function dedupeSelectedLayersForAI(
  layers: SelectedLayerForAI[]
): SelectedLayerForAI[] {
  const groups = new Map<string, SelectedLayerForAI[]>();
  for (const layer of layers) {
    const key = `${layer.kind}:${layer.id}`;
    const list = groups.get(key) ?? [];
    list.push(layer);
    groups.set(key, list);
  }

  const out: SelectedLayerForAI[] = [];
  for (const group of groups.values()) {
    const fullLayers = group.filter((l) => !l.attributeFilters?.length);
    if (fullLayers.length > 0) {
      out.push(fullLayers[fullLayers.length - 1]);
      continue;
    }
    const seen = new Set<string>();
    for (const layer of group) {
      const filter = layer.attributeFilters?.[0];
      const filterKey = filter
        ? `${filter.field}:${String(filter.value)}`
        : 'none';
      if (seen.has(filterKey)) continue;
      seen.add(filterKey);
      out.push(layer);
    }
  }
  return out;
}

/**
 * AI Layers selection is authoritative. Do not copy the map legend filter onto
 * full-layer selections — that re-applied Destroyed/purple after users switched
 * the data source to the whole feature layer. Category constraints must already
 * be on the selection (legend-class pick).
 */
export function withLegendClassFiltersForAI(
  layers: SelectedLayerForAI[],
  _legendClassFilterByLayerId?: Record<string, LegendClassFilter>
): SelectedLayerForAI[] {
  return dedupeSelectedLayersForAI(layers);
}

/**
 * Attach attribute-only feature snapshots to selected layers for chat/sitrep
 * AI. Enumerates the full layer when possible and marks featureSnapshotCoverage
 * so the backend can filter/rank against complete snapshots when ArcGIS
 * server-side fetch fails (e.g. userAuth). Geometry is omitted to keep POST
 * /disasterchat under the COMS 1mb body limit.
 */
export async function prefetchLayerFeaturesForAI(
  layers: SelectedLayerForAI[],
  options?: PrefetchLayerFeaturesOptions
): Promise<SelectedLayerForAI[]> {
  return Promise.all(
    layers.map(async (rawLayer) => {
      const layer = withTimeMeta(rawLayer, options?.timeFiltersByLayerId);
      try {
        if (
          layer.featureSnapshotCoverage === 'complete' &&
          Array.isArray(layer.featureSnapshot) &&
          layer.featureSnapshot.length > 0 &&
          layer.pickedFeatureName
        ) {
          return layer;
        }
        if (layer.kind === 'internal' && layer.versionId) {
          const { features, coverage } = await fetchInternalFeatures(
            layer.versionId,
            layer.spatialFilter
          );
          return {
            ...layer,
            featureSnapshot: features,
            ...(coverage ? { featureSnapshotCoverage: coverage } : {}),
          };
        }
        if (
          layer.kind === 'external' &&
          !isFeedUri(layer.serviceUrl) &&
          (layer.serviceUrl || options?.getAddedExternalLayers)
        ) {
          const { features, coverage } = await fetchExternalFeatures(
            layer,
            options
          );
          return {
            ...layer,
            ...(features.length > 0 || coverage
              ? { featureSnapshot: features }
              : {}),
            ...(coverage ? { featureSnapshotCoverage: coverage } : {}),
          };
        }
        return layer;
      } catch (error) {
        console.warn(
          `[prefetchLayerFeaturesForAI] Failed for ${layer.kind} ${layer.id}:`,
          error
        );
        // Do not claim complete coverage after a failed query.
        const {
          featureSnapshot: _s,
          featureSnapshotCoverage: _c,
          ...rest
        } = layer;
        return rest;
      }
    })
  );
}
